import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Activity, Globe2, Monitor, Shield } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createPSPixel } from "@/lib/api";
import PortalNotesFields from "@/components/PortalNotesFields";

const FEATURES = [
  { icon: Globe2,  title: "Geo location",   sub: "Country, region, city, ISP" },
  { icon: Monitor, title: "Device & browser", sub: "Desktop / Mobile / Bot, Chrome / Safari / Firefox, OS" },
  { icon: Activity, title: "Rich traffic",  sub: "Page URL, referrer, viewport, new vs returning" },
  { icon: Shield,  title: "First-party",    sub: "No third-party cookies, no data leaves your tenant" },
];

export default function PSPixelBuilder() {
  const nav = useNavigate();
  const [name, setName] = useState("Homepage tracking");
  const [portals, setPortals] = useState([]);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const res = await createPSPixel({
        name: name.trim(), portals, notes, tags, folder_id: folderId,
      });
      toast.success("PortalStudio tracking pixel created");
      nav(`/success/ps_pixel/${res.id}`);
    } catch {
      toast.error("Failed to create");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">New Component</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
            PortalStudio Tracking Pixel
          </h1>
          <p className="text-zinc-600 mt-2">
            First-party analytics owned by you. Fires a 1×1 transparent GIF, logs rich visitor data inside PortalStudio.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <form onSubmit={onSubmit} data-testid="builder-form" className="md:col-span-7 space-y-8">
            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Pixel</div>
              <div>
                <Label className="text-xs text-zinc-500">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1.5" data-testid="ps-pixel-name-input" />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Internal label. Pick something you'll recognise in the dashboard (e.g. "Parent Portal Home").
                </p>
              </div>
            </section>

            <PortalNotesFields portals={portals} setPortals={setPortals}
                              notes={notes} setNotes={setNotes}
                              tags={tags} setTags={setTags}
                              folderId={folderId} setFolderId={setFolderId} />

            <Button type="submit" disabled={submitting} data-testid="generate-embed-btn"
              className="h-12 px-6 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all duration-200">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create tracking pixel
            </Button>
          </form>

          <div className="md:col-span-5">
            <div className="md:sticky md:top-24 space-y-3">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-1">What gets tracked</div>
              <div className="border border-zinc-200 rounded-md bg-white divide-y divide-zinc-100">
                {FEATURES.map((f) => (
                  <div key={f.title} className="p-4 flex gap-3">
                    <div className="h-9 w-9 rounded-md grid place-items-center bg-zinc-100">
                      <f.icon className="h-4 w-4 text-zinc-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-zinc-950">{f.title}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{f.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed px-1">
                After creation you'll get a 1×1 GIF URL + snippet. Embed it on any page and see live analytics at
                <span className="font-mono text-zinc-700"> /pixel/&lt;id&gt;/analytics</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
